/*
 * announcement-marquee.js
 * Infinite, seamless running-text bar for the announcement section.
 *
 * The section renders the announcement list twice. This element:
 *   1. clones the group until the track is at least 2x the container width
 *      (so short content never leaves a visible gap on wide screens),
 *   2. measures one group and sets --marquee-shift + --marquee-duration so the
 *      scroll speed stays constant (px/second) no matter how much text there is.
 *
 * Falls back gracefully: with JS disabled the CSS defaults (-50% over 30s) still loop.
 */
if (!customElements.get('announcement-marquee')) {
  customElements.define(
    'announcement-marquee',
    class AnnouncementMarquee extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.announcement-bar__track');
        this.viewport = this.querySelector('.announcement-bar__marquee');
        this.onResize = this.debounce(this.measure.bind(this), 150);
      }

      connectedCallback() {
        if (!this.track || !this.viewport) return;

        // Keep a pristine copy of one group to clone from.
        const firstGroup = this.track.querySelector('.announcement-bar__group');
        if (!firstGroup) return;
        this.groupTemplate = firstGroup.cloneNode(true);
        this.groupTemplate.setAttribute('aria-hidden', 'true');
        this.groupTemplate.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        // Cloned copies must not be re-registered by the theme editor.
        this.groupTemplate.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });

        this.measure();

        window.addEventListener('resize', this.onResize);

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.measure());
        }
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
      }

      measure() {
        if (!this.track) return;

        const groups = () => this.track.querySelectorAll('.announcement-bar__group');
        const groupWidth = groups()[0].getBoundingClientRect().width;
        if (!groupWidth) return;

        const target = Math.max(this.viewport.getBoundingClientRect().width * 2, groupWidth * 2);
        const needed = Math.max(2, Math.ceil(target / groupWidth));

        let current = groups().length;
        while (current < needed && current < 40) {
          this.track.appendChild(this.groupTemplate.cloneNode(true));
          current += 1;
        }
        while (current > needed && current > 2) {
          this.track.removeChild(this.track.lastElementChild);
          current -= 1;
        }

        // Travelling exactly one group width puts an identical group in its place.
        const speed = Math.max(parseFloat(this.dataset.speed) || 60, 5);
        this.style.setProperty('--marquee-shift', `-${groupWidth}px`);
        this.style.setProperty('--marquee-duration', `${groupWidth / speed}s`);
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    }
  );
}
